'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Section } from '@/components/ui/Section';
import { Container } from '@/components/ui/Container';
import { Heading } from '@/components/ui/Heading';
import { Text } from '@/components/ui/Text';
import { normalizePartnerName } from '@/lib/image-alt-helper';
import { type Locale } from '@/lib/i18n';
import { getIntegrationOrPartnerPath } from '@/lib/routes';
import styles from './IntegrationsGrid.module.css';

interface Integration {
  name: string;
  logo: string;
  category: 'pos' | 'payment' | 'loyalty' | 'pos-partners' | 'hardware';
  reseller?: string;
  resellerLogo?: string;
  recommended?: boolean;
  website?: string;
  hasDedicatedPage?: boolean; // New: indicates if this integration has a dedicated page
  slug?: string; // New: URL slug for the dedicated page
  isPartner?: boolean; // New: indicates if this is a partner (uses /partners/ URL instead of /integrations/)
}

interface IntegrationDetails {
  name: string;
  description: string;
  resellers?: Array<{
    name: string;
    logo: string;
  }>;
  capabilities?: string[];
  features?: string[];
}

interface IntegrationsGridProps {
  translations: any;
  altTexts?: Record<string, string>;
  locale: Locale;
}

const integrations: Integration[] = [
  // POS Systems
  { name: 'Lightspeed', logo: '/images/integrations/pos/lightspeed.webp', category: 'pos', website: 'https://www.lightspeedhq.com/', hasDedicatedPage: true, slug: 'lightspeed' },
  { name: 'unTill', logo: '/images/integrations/pos/untill.webp', category: 'pos', website: 'https://www.untill.com/', hasDedicatedPage: true, slug: 'untill' },
  { name: 'MplusKASSA', logo: '/images/integrations/pos/mpluskassa.webp', category: 'pos', website: 'https://www.mpluskassa.nl/', hasDedicatedPage: true, slug: 'mpluskassa' },
  { name: 'Vectron', logo: '/images/integrations/pos/vectron.webp', category: 'pos', website: 'https://www.vectron-systems.com/', hasDedicatedPage: true, slug: 'vectron' },
  { name: 'Trivec', logo: '/images/integrations/pos/trivec.webp', category: 'pos', website: 'https://www.trivec.se/', hasDedicatedPage: true, slug: 'trivec' },
  { name: 'Oracle', logo: '/images/integrations/pos/oracle.webp', category: 'pos', reseller: 'Van Hessen', resellerLogo: '/images/integrations/pos/van-hessen.webp', website: 'https://www.oracle.com/', hasDedicatedPage: true, slug: 'oracle' },
  { name: 'Deliverect', logo: '/images/integrations/pos/deliverect.webp', category: 'pos', website: 'https://www.deliverect.com/', hasDedicatedPage: true, slug: 'deliverect' },
  { name: 'Gastrofix', logo: '/images/integrations/pos/gastrofix.webp', category: 'pos', website: 'https://www.gastrofix.com/', hasDedicatedPage: true, slug: 'gastrofix' },
  { name: 'NuvoPOS', logo: '/images/integrations/pos/nuvopos.webp', category: 'pos', website: 'https://www.nuvopos.com/', hasDedicatedPage: true, slug: 'nuvopos' },
  { name: 'Twelve', logo: '/images/integrations/pos/twelve.webp', category: 'pos', website: 'https://www.twelve.eu/', hasDedicatedPage: true, slug: 'twelve' },
  { name: 'NCR Aloha', logo: '/images/integrations/pos/ncr-aloha.webp', category: 'pos', website: 'https://www.ncr.com/', hasDedicatedPage: true, slug: 'ncr-aloha' },
  { name: 'CashDesk', logo: '/images/integrations/pos/cashdesk.webp', category: 'pos', website: 'https://www.cashdesk.nl/', hasDedicatedPage: true, slug: 'cashdesk' },
  { name: 'Mr. Winston', logo: '/images/integrations/pos/mr-winston.webp', category: 'pos', website: 'https://winstonpos.com/', hasDedicatedPage: true, slug: 'mr-winston' },
  { name: 'Hypersoft', logo: '/images/integrations/pos/hypersoft.webp', category: 'pos', website: 'https://www.hypersoft.de/', hasDedicatedPage: true, slug: 'hypersoft' },
  
  // Payment Providers
  { name: 'Mollie', logo: '/images/integrations/payment/mollie.webp', category: 'payment', recommended: true, website: 'https://www.mollie.com/', hasDedicatedPage: true, slug: 'mollie' },
  { name: 'CM', logo: '/images/integrations/payment/cm.webp', category: 'payment', recommended: true, website: 'https://www.cm.com/', hasDedicatedPage: true, slug: 'cm' },
  { name: 'PAY', logo: '/images/integrations/payment/pay.webp', category: 'payment', website: 'https://www.pay.nl/', hasDedicatedPage: true, slug: 'pay' },
  { name: 'Adyen', logo: '/images/integrations/payment/adyen.webp', category: 'payment', website: 'https://www.adyen.com/', hasDedicatedPage: true, slug: 'adyen' },
  
  // Loyalty
  { name: 'Leat', logo: '/images/integrations/loyalty/leat.webp', category: 'loyalty', recommended: true, website: 'https://www.leat.com/', hasDedicatedPage: true, slug: 'leat' },
  
  // POS Partners (Resellers) - unTill Partners
  { name: 'Janssens Kassasystemen', logo: '/images/integrations/pos/janssens-kassasystemen.webp', category: 'pos-partners', website: 'https://kassasystemen.nl/', hasDedicatedPage: true, slug: 'janssens-kassasystemen', isPartner: true },
  { name: 'MS POS', logo: '/images/integrations/pos/ms-pos.webp', category: 'pos-partners', website: 'https://ms-pos.net/', hasDedicatedPage: true, slug: 'ms-pos', isPartner: true },
  { name: 'Adnamics', logo: '/images/integrations/pos/adnamics-reseller-untill.webp', category: 'pos-partners', website: 'https://www.adnamics.nl/', hasDedicatedPage: true, slug: 'adnamics', isPartner: true },
  { name: 'Upta', logo: '/images/integrations/pos/upta-reseller-untill.webp', category: 'pos-partners', website: 'https://upta.nl/', hasDedicatedPage: true, slug: 'upta', isPartner: true },
  
  // POS Partners (Resellers) - MplusKASSA Partners
  { name: 'Kassanet-Pieterse', logo: '/images/integrations/pos/kassanet-pieterse.webp', category: 'pos-partners', website: 'https://www.pieterse.nl/', hasDedicatedPage: true, slug: 'kassanet-pieterse', isPartner: true },
  { name: 'Konacom', logo: '/images/integrations/pos/konacom-reseller-lightspeed-mplus.webp', category: 'pos-partners', website: 'https://konacom.nl/', hasDedicatedPage: true, slug: 'konacom', isPartner: true },
  { name: 'Opsomer', logo: '/images/integrations/pos/opsomer-reseller-mplus.webp', category: 'pos-partners', website: 'https://www.opsomerbvba.be/', hasDedicatedPage: true, slug: 'opsomer', isPartner: true },
  { name: 'Evers Kassasystemen', logo: '/images/integrations/pos/evers-kassasystemen-reseller-mplus.webp', category: 'pos-partners', website: 'https://www.evers-kassasystemen.nl/', hasDedicatedPage: true, slug: 'evers-kassasystemen', isPartner: true },
  { name: 'STN', logo: '/images/integrations/pos/stn-reseller-mplus.webp', category: 'pos-partners', website: 'https://www.stnkassa.nl/', hasDedicatedPage: true, slug: 'stn', isPartner: true },
  { name: 'Kassanet Nuus', logo: '/images/integrations/pos/kassanet-nuus-reseller-mplus-vectron.webp', category: 'pos-partners', website: 'https://www.nuus.nl/', hasDedicatedPage: true, slug: 'kassanet-nuus', isPartner: true },
  
  // POS Partners (Resellers) - Lightspeed Partners
  { name: 'POSsystems', logo: '/images/integrations/pos/possystems-reseller-lightspeed.webp', category: 'pos-partners', website: 'https://possystems.nl/', hasDedicatedPage: true, slug: 'possystems', isPartner: true },
  { name: 'Cecon', logo: '/images/integrations/pos/cecon-reseller-lightspeed.webp', category: 'pos-partners', website: 'https://www.cecon.de/', hasDedicatedPage: true, slug: 'cecon', isPartner: true },
  
  // POS Partners (Resellers) - Trivec Partners
  { name: 'Bork', logo: '/images/integrations/pos/bork.webp', category: 'pos-partners', website: 'https://bork.nl/', hasDedicatedPage: true, slug: 'bork', isPartner: true },
  
  // POS Partners (Resellers) - Oracle Partners
  { name: 'Van Hessen', logo: '/images/integrations/pos/van-hessen.webp', category: 'pos-partners', website: 'https://www.vanhessen.nl/', hasDedicatedPage: true, slug: 'van-hessen', isPartner: true },
  
  // Hardware
  { name: 'Prestop', logo: '/images/integrations/hardware/prestop.webp', category: 'hardware', website: 'https://www.prestop.nl/', hasDedicatedPage: true, slug: 'prestop', isPartner: true },
  { name: 'Acrelec', logo: '/images/integrations/hardware/acrelec.webp', category: 'hardware', website: 'https://www.acrelec.com/', hasDedicatedPage: true, slug: 'acrelec', isPartner: true },
  { name: 'Qiox', logo: '/images/integrations/hardware/qiox-hardware.webp', category: 'hardware', website: 'https://www.qiox.nl/', hasDedicatedPage: true, slug: 'qiox', isPartner: true },
];

export function IntegrationsGrid({ translations, altTexts, locale }: IntegrationsGridProps) {
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: translations.categories.all },
    { id: 'pos', label: translations.categories.pos },
    { id: 'payment', label: translations.categories.payment },
    { id: 'loyalty', label: translations.categories.loyalty },
    { id: 'pos-partners', label: translations.categories.posPartners },
    { id: 'hardware', label: translations.categories.hardware },
  ];

  const scrollToSection = (categoryId: string) => {
    setActiveCategory(categoryId);
    
    if (categoryId === 'all') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const sectionId = `section-${categoryId}`;
    const element = document.getElementById(sectionId);
    
    if (element) {
      const offset = 100; // Offset voor de header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const getIntegrationDetails = (name: string): IntegrationDetails | null => {
    const details = translations.integrationDetails[name.toLowerCase().replace(/\s+/g, '-')];
    return details || null;
  };

  const openModal = (integrationName: string) => {
    setSelectedIntegration(integrationName);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedIntegration(null);
    document.body.style.overflow = '';
  };

  // ESC key listener for closing modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedIntegration) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [selectedIntegration]);

  const selectedDetails = selectedIntegration ? getIntegrationDetails(selectedIntegration) : null;
  const selectedIntegrationData = integrations.find(int => int.name === selectedIntegration);

  return (
    <>
      {/* Category Filter */}
      <Section background="secondary" className={styles.filterSection}>
        <Container>
          <div className={styles.categoryFilter}>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`${styles.categoryButton} ${activeCategory === cat.id ? styles.active : ''}`}
                onClick={() => scrollToSection(cat.id)}
              >
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </Container>
      </Section>

      {/* POS Systems Section */}
      <Section background="primary" id="section-pos">
        <Container>
          <div className={styles.sectionHeader}>
              <Heading as="h2" className={styles.sectionTitle}>
                {translations.categories.pos}
              </Heading>
              <Text size="lg" color="secondary">
                {translations.categoryDescriptions.pos}
              </Text>
            </div>

            <div className={styles.integrationsGrid}>
              {integrations.filter(int => int.category === 'pos').map((integration, index) => {
                const hasPage = integration.hasDedicatedPage && integration.slug;

                return hasPage ? (
                  <Link
                    key={integration.name}
                    href={getIntegrationOrPartnerPath(integration.slug!, locale)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    {integration.reseller && integration.resellerLogo && (
                      <div className={styles.resellerBadge}>
                        <Image
                          src={integration.resellerLogo}
                          alt={integration.reseller}
                          width={80}
                          height={40}
                          className={styles.resellerLogo}
                          style={{ objectFit: 'contain' }}
                        />
                      </div>
                    )}
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </Link>
                ) : (
                  <div
                    key={integration.name}
                    onClick={() => openModal(integration.name)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s`, cursor: 'pointer' }}
                  >
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    {integration.reseller && integration.resellerLogo && (
                      <div className={styles.resellerBadge}>
                        <Image
                          src={integration.resellerLogo}
                          alt={integration.reseller}
                          width={80}
                          height={40}
                          className={styles.resellerLogo}
                          style={{ objectFit: 'contain' }}
                        />
                      </div>
                    )}
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Container>
        </Section>

      {/* Payment Providers Section */}
      <Section background="secondary" id="section-payment">
        <Container>
          <div className={styles.sectionHeader}>
              <Heading as="h2" className={styles.sectionTitle}>
                {translations.categories.payment}
              </Heading>
              <Text size="lg" color="secondary">
                {translations.categoryDescriptions.payment}
              </Text>
            </div>

            <div className={styles.integrationsGrid}>
              {integrations.filter(int => int.category === 'payment').map((integration, index) => {
                const hasPage = integration.hasDedicatedPage && integration.slug;

                return hasPage ? (
                  <Link
                    key={integration.name}
                    href={getIntegrationOrPartnerPath(integration.slug!, locale)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    {integration.recommended && (
                      <div className={styles.recommendedBadge} data-text={translations.recommended || 'RECOMMENDED'}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      </div>
                    )}
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </Link>
                ) : (
                  <div
                    key={integration.name}
                    onClick={() => openModal(integration.name)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s`, cursor: 'pointer' }}
                  >
                    {integration.recommended && (
                      <div className={styles.recommendedBadge} data-text={translations.recommended || 'RECOMMENDED'}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      </div>
                    )}
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Container>
        </Section>

      {/* Loyalty Section */}
      <Section background="primary" id="section-loyalty">
        <Container>
          <div className={styles.sectionHeader}>
              <Heading as="h2" className={styles.sectionTitle}>
                {translations.categories.loyalty}
              </Heading>
              <Text size="lg" color="secondary">
                {translations.categoryDescriptions.loyalty}
              </Text>
            </div>

            <div className={styles.integrationsGrid}>
              {integrations.filter(int => int.category === 'loyalty').map((integration, index) => {
                const hasPage = integration.hasDedicatedPage && integration.slug;

                return hasPage ? (
                  <Link
                    key={integration.name}
                    href={getIntegrationOrPartnerPath(integration.slug!, locale)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    {integration.recommended && (
                      <div className={styles.recommendedBadge} data-text={translations.recommended || 'RECOMMENDED'}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      </div>
                    )}
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </Link>
                ) : (
                  <div
                    key={integration.name}
                    onClick={() => openModal(integration.name)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s`, cursor: 'pointer' }}
                  >
                    {integration.recommended && (
                      <div className={styles.recommendedBadge} data-text={translations.recommended || 'RECOMMENDED'}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      </div>
                    )}
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Container>
        </Section>

      {/* POS Partners Section */}
      <Section background="secondary" id="section-pos-partners">
        <Container>
          <div className={styles.sectionHeader}>
              <Heading as="h2" className={styles.sectionTitle}>
                {translations.categories.posPartners}
              </Heading>
              <Text size="lg" color="secondary">
                {translations.categoryDescriptions.posPartners}
              </Text>
            </div>

            <div className={styles.integrationsGrid}>
              {integrations.filter(int => int.category === 'pos-partners').map((integration, index) => {
                const hasPage = integration.hasDedicatedPage && integration.slug;

                return hasPage ? (
                  <Link
                    key={integration.name}
                    href={getIntegrationOrPartnerPath(integration.slug!, locale)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    {integration.reseller && integration.resellerLogo && (
                      <div className={styles.resellerBadge}>
                        <Image
                          src={integration.resellerLogo}
                          alt={integration.reseller}
                          width={80}
                          height={40}
                          className={styles.resellerLogo}
                          style={{ objectFit: 'contain' }}
                        />
                      </div>
                    )}
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </Link>
                ) : (
                  <div
                    key={integration.name}
                    onClick={() => openModal(integration.name)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s`, cursor: 'pointer' }}
                  >
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    {integration.reseller && integration.resellerLogo && (
                      <div className={styles.resellerBadge}>
                        <Image
                          src={integration.resellerLogo}
                          alt={integration.reseller}
                          width={80}
                          height={40}
                          className={styles.resellerLogo}
                          style={{ objectFit: 'contain' }}
                        />
                      </div>
                    )}
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Container>
        </Section>

      {/* Hardware Section */}
      <Section background="primary" id="section-hardware">
        <Container>
          <div className={styles.sectionHeader}>
              <Heading as="h2" className={styles.sectionTitle}>
                {translations.categories.hardware}
              </Heading>
              <Text size="lg" color="secondary">
                {translations.categoryDescriptions.hardware}
              </Text>
            </div>

            <div className={styles.integrationsGrid}>
              {integrations.filter(int => int.category === 'hardware').map((integration, index) => {
                const hasPage = integration.hasDedicatedPage && integration.slug;

                return hasPage ? (
                  <Link
                    key={integration.name}
                    href={getIntegrationOrPartnerPath(integration.slug!, locale)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </Link>
                ) : (
                  <div
                    key={integration.name}
                    onClick={() => openModal(integration.name)}
                    className={styles.integrationCard}
                    style={{ animationDelay: `${index * 0.05}s`, cursor: 'pointer' }}
                  >
                    <div className={styles.integrationLogoWrapper}>
                      <Image
                        src={integration.logo}
                        alt={altTexts?.[normalizePartnerName(integration.name)] || `${integration.name} integration`}
                        width={200}
                        height={100}
                        className={styles.integrationLogo}
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.integrationName}>{integration.name}</span>
                    <div className={styles.cardOverlay}>
                      <span className={styles.viewDetails}>{translations.viewDetails || 'View Details'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Container>
        </Section>

      {/* Modal */}
      {selectedIntegration && selectedDetails && selectedIntegrationData && (
        <div className={styles.modal} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={closeModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <div className={styles.modalHeader}>
              <div className={styles.modalLogo}>
                <Image
                  src={selectedIntegrationData.logo}
                  alt={selectedIntegration}
                  width={200}
                  height={100}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <h3 className={styles.modalTitle}>{selectedIntegration}</h3>
            </div>

            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>{selectedDetails.description}</p>

              {selectedDetails.resellers && selectedDetails.resellers.length > 0 && (
                <div className={styles.resellersSection}>
                  <h4>
                    {selectedIntegrationData?.category === 'pos-partners' 
                      ? (translations.integratedWith || 'Integrated with')
                      : (translations.resellers || 'Resellers')}
                  </h4>
                  <div className={styles.resellersList}>
                    {selectedDetails.resellers.map((reseller) => (
                      <div key={reseller.name} className={styles.resellerItem}>
                        <Image
                          src={reseller.logo}
                          alt={reseller.name}
                          width={120}
                          height={60}
                          style={{ objectFit: 'contain' }}
                        />
                        <span>{reseller.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedDetails.capabilities && selectedDetails.capabilities.length > 0 && (
                <div className={styles.capabilitiesSection}>
                  <h4>{translations.capabilities || 'Capabilities'}</h4>
                  <ul className={styles.capabilitiesList}>
                    {selectedDetails.capabilities.map((capability, index) => (
                      <li key={index}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="var(--color-accent-orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {capability}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedDetails.features && selectedDetails.features.length > 0 && (
                <div className={styles.featuresSection}>
                  <h4>{translations.features || 'Features'}</h4>
                  <div className={styles.featuresTags}>
                    {selectedDetails.features.map((feature, index) => (
                      <span key={index} className={styles.featureTag}>
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedIntegrationData?.website && (
                <div className={styles.websiteButtonSection}>
                  <a 
                    href={selectedIntegrationData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.websiteButton}
                  >
                    {translations.visitWebsite || 'Visit website'}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 10.8333V15.8333C15 16.2754 14.8244 16.6993 14.5118 17.0118C14.1993 17.3244 13.7754 17.5 13.3333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V6.66667C2.5 6.22464 2.67559 5.80072 2.98816 5.48816C3.30072 5.17559 3.72464 5 4.16667 5H9.16667M12.5 2.5H17.5M17.5 2.5V7.5M17.5 2.5L8.33333 11.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

